import { sessionStore } from "@/auth/sessionStore";
import { AuthRequiredError } from "@/auth/errors/authErrors";
import apiClient from "./api.client";
import type { Endpoint } from "./contracts/types";

/**
 * Generic API caller that works with endpoint contracts.
 *
 * - Auth: fast-fails if endpoint requires auth and no token exists.
 *   Token attachment and 401 refresh are handled by apiClient interceptors.
 * - Zod schema: if the endpoint has a `schema`, it parses/transforms the response.
 * - Auto-unwrap: otherwise, unwraps the common `{ data: T }` ResponseBuilder wrapper.
 */
export async function callApi<TOutput>(
  endpoint: Endpoint,
  options?: { data?: unknown; params?: unknown },
): Promise<TOutput> {
  if (endpoint.auth && !sessionStore.hasAccessToken()) {
    throw new AuthRequiredError(endpoint.path);
  }

  const res = await apiClient.request({
    method: endpoint.method,
    url: endpoint.path,
    ...(options?.data != null && { data: options.data }),
    ...(options?.params != null && { params: options.params }),
  });

  if (endpoint.schema) {
    return endpoint.schema.parse(res.data) as TOutput;
  }

  const body = res.data;
  if (body && typeof body === "object" && "data" in body) {
    return (body as { data: TOutput }).data;
  }
  return body as TOutput;
}
